// ⚠️ GENERATED COPY — DO NOT EDIT.
// Source: shared/ui/survey/widgets/PharmacySearchWidget.tsx — edit there, then run: bash scripts/sync_shared_code.sh
// CI verifies these copies match the source (sync_shared_code.sh --check).
import React, { useState, useEffect, useRef } from "react";

interface PharmacyResult {
  id?: string;
  name: string;
  contact?: { phone?: string; fax?: string; email?: string };
  address?: { formatted?: string; street?: string };
  [key: string]: unknown;
}

interface PharmacySearchWidgetProps {
  value?: string;
  onChange: (value: string) => void;
  mode?: string;
  /** Values are answer keys: field indices (survey flow, nullable) or profile
   * key strings (portal). Only ever used as object keys, so numbers coerce. */
  pharmacyFieldMapping?: Record<string, string | number | null>;
  onBatchFill?: (fills: Record<string, string>) => void;
}

/**
 * Pharmacy search autocomplete widget.
 * Calls /api/pharmacy/search to find pharmacies by name/city.
 * When a pharmacy is selected, populates all pharmacy fields at once.
 */
export default function PharmacySearchWidget({ value, onChange, mode, pharmacyFieldMapping, onBatchFill }: PharmacySearchWidgetProps) {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState<PharmacyResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedPharmacy, setSelectedPharmacy] = useState<PharmacyResult | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const searchPharmacies = async (searchQuery: string) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`/api/pharmacy/search?query=${encodeURIComponent(searchQuery)}&limit=8`);
      if (res.ok) {
        const data = await res.json();
        setResults(data.pharmacies || []);
        setShowDropdown(true);
      }
    } catch (err) {
      console.warn("Pharmacy search failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    setSelectedPharmacy(null);
    // Update parent immediately so clearing works
    onChange(val);

    // Debounce search
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchPharmacies(val), 300);
  };

  const handleSelect = (pharmacy: PharmacyResult) => {
    setSelectedPharmacy(pharmacy);
    setQuery(pharmacy.name);
    setShowDropdown(false);

    // Set the main value (pharmacy name)
    onChange(pharmacy.name);

    // Batch-fill related pharmacy fields
    if (onBatchFill && pharmacyFieldMapping) {
      const fills: Record<string, string> = {};
      if (pharmacyFieldMapping.name != null) fills[pharmacyFieldMapping.name] = pharmacy.name;
      if (pharmacyFieldMapping.phone != null) fills[pharmacyFieldMapping.phone] = pharmacy.contact?.phone || "";
      if (pharmacyFieldMapping.fax != null) fills[pharmacyFieldMapping.fax] = pharmacy.contact?.fax || "";
      if (pharmacyFieldMapping.email != null) fills[pharmacyFieldMapping.email] = pharmacy.contact?.email || "";
      if (pharmacyFieldMapping.address != null) fills[pharmacyFieldMapping.address] = pharmacy.address?.formatted || "";
      onBatchFill(fills);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: mode === "patient" ? "14px 16px" : "10px 12px",
    fontSize: mode === "patient" ? 18 : 14,
    border: "2px solid #d8c9b6",
    borderRadius: 8,
    outline: "none",
    fontFamily: "system-ui, -apple-system, sans-serif",
    color: "#182a33",
    boxSizing: "border-box",
  };

  return (
    <div ref={dropdownRef} style={{ position: "relative" }}>
      <input
        type="text"
        value={query}
        onChange={handleInputChange}
        onFocus={() => results.length > 0 && setShowDropdown(true)}
        placeholder="Start typing your pharmacy name or city..."
        style={inputStyle}
      />
      {loading && (
        <div style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: "#5b6e7c", fontSize: 13 }}>
          Searching...
        </div>
      )}

      {/* Dropdown results */}
      {showDropdown && results.length > 0 && (
        <div style={{
          position: "absolute",
          top: "100%",
          left: 0,
          right: 0,
          background: "white",
          border: "2px solid #d8c9b6",
          borderTop: "none",
          borderRadius: "0 0 8px 8px",
          maxHeight: 320,
          overflowY: "auto",
          zIndex: 100,
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}>
          {results.map((pharmacy, i) => (
            <div
              key={pharmacy.id || i}
              onClick={() => handleSelect(pharmacy)}
              style={{
                padding: "12px 16px",
                cursor: "pointer",
                borderBottom: i < results.length - 1 ? "1px solid #ede6dc" : "none",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#f5f0eb"}
              onMouseLeave={(e) => e.currentTarget.style.background = "white"}
            >
              <div style={{ fontWeight: 600, color: "#182a33", fontSize: mode === "patient" ? 16 : 14 }}>
                {pharmacy.name}
              </div>
              <div style={{ color: "#5b6e7c", fontSize: mode === "patient" ? 14 : 12, marginTop: 2 }}>
                {pharmacy.address?.formatted || pharmacy.address?.street || ""}
              </div>
              {pharmacy.contact?.phone && (
                <div style={{ color: "#5b6e7c", fontSize: mode === "patient" ? 13 : 11, marginTop: 2 }}>
                  Phone: {pharmacy.contact.phone}
                  {pharmacy.contact.fax && ` | Fax: ${pharmacy.contact.fax}`}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* No results message */}
      {showDropdown && results.length === 0 && query.length >= 2 && !loading && (
        <div style={{
          position: "absolute",
          top: "100%",
          left: 0,
          right: 0,
          background: "white",
          border: "2px solid #d8c9b6",
          borderTop: "none",
          borderRadius: "0 0 8px 8px",
          padding: "16px",
          color: "#5b6e7c",
          fontSize: 14,
          zIndex: 100,
        }}>
          No pharmacies found. You can type the pharmacy name manually.
        </div>
      )}

      {/* Selected pharmacy card */}
      {selectedPharmacy && (
        <div style={{
          marginTop: 12,
          padding: "12px 16px",
          background: "#eef4f2",
          borderRadius: 8,
          border: "1px solid #bbd5cc",
        }}>
          <div style={{ fontWeight: 600, color: "#6b9c8e", fontSize: 15, marginBottom: 4 }}>
            {selectedPharmacy.name}
          </div>
          <div style={{ color: "#555", fontSize: 13 }}>
            {selectedPharmacy.address?.formatted}
          </div>
          {selectedPharmacy.contact?.phone && (
            <div style={{ color: "#555", fontSize: 13, marginTop: 2 }}>
              Phone: {selectedPharmacy.contact.phone}
              {selectedPharmacy.contact.fax && ` | Fax: ${selectedPharmacy.contact.fax}`}
            </div>
          )}
          <div style={{ fontSize: 12, color: "#6b9c8e", marginTop: 6, fontStyle: "italic" }}>
            Pharmacy details will be filled in automatically
          </div>
        </div>
      )}
    </div>
  );
}
